import { useState, useRef, useEffect } from 'react';
import { Bot, Send, Loader2, Copy, Sparkles, User, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { API } from './constants';

const QUICK_ACTIONS = [
  { label: 'Email premier contact', prompt: 'Redige un email de premier contact pour ce prospect' },
  { label: 'Script appel', prompt: 'Prepare-moi un script d\'appel telephonique pour ce prospect avec les objections possibles' },
  { label: 'Email relance', prompt: 'Redige un email de relance pour ce prospect' },
  { label: 'Strategie conversion', prompt: 'Analyse ce prospect et propose-moi une strategie pour le convertir' },
  { label: 'Idees de sujets', prompt: 'Propose-moi 5 accroches/sujets d\'email originaux pour ce prospect' },
];

const GENERAL_ACTIONS = [
  { label: 'Bilan prospection', prompt: 'Fais-moi un bilan de ma prospection avec des recommandations' },
  { label: 'Prochaines actions', prompt: 'Quelles sont les 5 actions commerciales prioritaires que je devrais faire cette semaine ?' },
  { label: 'Strategie CE', prompt: 'Comment approcher les CE d\'entreprises de ma region ? Donne-moi un plan d\'action concret' },
  { label: 'Idees evenements', prompt: 'Propose-moi 5 idees d\'evenements ou offres speciales que je pourrais proposer a mes prospects cette saison' },
];

export function CrmAIAssistant({ selectedProspect }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `crm-ai-${Date.now()}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;

    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/crm/ai-assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          prospect_id: selectedProspect?.id || null,
          session_id: sessionId,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: `Erreur: ${data.detail || 'Impossible de repondre'}`, error: true }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Erreur de connexion', error: true }]);
    }
    setLoading(false);
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copie !');
  };

  const actions = selectedProspect ? QUICK_ACTIONS : GENERAL_ACTIONS;

  return (
    <div className="flex flex-col h-[70vh] max-h-[600px]" data-testid="crm-ai-assistant">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b border-gray-100 mb-3">
        <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
          <Bot size={16} className="text-purple-500" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold text-gray-800">Assistant Commercial IA</p>
          <p className="text-[9px] text-gray-400">
            {selectedProspect ? `Prospect: ${selectedProspect.nom}` : 'Mode general — selectionnez un prospect pour personnaliser'}
          </p>
        </div>
        {messages.length > 0 && (
          <button onClick={() => setMessages([])} className="p-1.5 rounded-md hover:bg-gray-50 text-gray-300 hover:text-gray-500 transition-colors" title="Effacer" data-testid="clear-chat">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <Sparkles size={24} className="text-purple-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500 font-medium">Comment puis-je vous aider ?</p>
              <p className="text-[9px] text-gray-400 mt-0.5">
                {selectedProspect
                  ? `Je connais ${selectedProspect.nom} — posez-moi vos questions`
                  : 'Strategie, emails, scripts, analyse... Je suis la pour vous'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {actions.map((a, i) => (
                <button key={i} onClick={() => sendMessage(a.prompt)}
                  className="text-left p-2.5 rounded-lg border border-gray-100 hover:border-purple-200 hover:bg-purple-50/50 transition-all text-[10px] text-gray-600 leading-snug"
                  data-testid={`quick-action-${i}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                msg.role === 'user'
                  ? 'bg-[#1B2A52] text-white rounded-br-md'
                  : msg.error
                    ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-md'
                    : 'bg-gray-50 text-gray-700 border border-gray-100 rounded-bl-md'
              }`}>
                <div className="text-[11px] leading-relaxed whitespace-pre-wrap">{msg.text}</div>
                {msg.role === 'assistant' && !msg.error && (
                  <button onClick={() => copyText(msg.text)}
                    className="mt-1.5 flex items-center gap-1 text-[9px] text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid={`copy-msg-${i}`}>
                    <Copy size={10} /> Copier
                  </button>
                )}
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 size={12} className="animate-spin text-purple-400" />
                <span className="text-[10px] text-gray-400">Reflexion...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2 pt-3 mt-3 border-t border-gray-100">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder={selectedProspect ? `Demandez quelque chose sur ${selectedProspect.nom}...` : 'Posez votre question commerciale...'}
          className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-purple-300 focus:ring-1 focus:ring-purple-100 transition-all"
          disabled={loading}
          data-testid="ai-chat-input"
        />
        <Button onClick={() => sendMessage()} disabled={loading || !input.trim()} size="sm"
          className="bg-purple-500 hover:bg-purple-600 text-white rounded-xl px-3 h-9"
          data-testid="ai-chat-send">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </div>
    </div>
  );
}
